import { reactive } from 'vue'
import * as Api from './permissions_api'

const keyFor = ({ resourceType, resourceId }) => `${resourceType}:${resourceId}`
const tempId = () => `tmp_${Date.now()}_${Math.random().toString(36).slice(2)}`

const state = reactive({
  permissionsByResource: {},
  loadingByResource: {},
  loadedAtByResource: {},
  errorByResource: {}
})

const inflight = {}

const clone = (value) => JSON.parse(JSON.stringify(value))

const replacePermission = (resourceKey, permissionId, nextPermission) => {
  const rows = state.permissionsByResource[resourceKey] || []
  const idx = rows.findIndex((p) => String(p.id) === String(permissionId))

  if (idx >= 0) {
    rows.splice(idx, 1, nextPermission)
  }
}

const removePermission = (resourceKey, permissionId) => {
  const rows = state.permissionsByResource[resourceKey] || []
  const idx = rows.findIndex((p) => String(p.id) === String(permissionId))

  if (idx >= 0) {
    rows.splice(idx, 1)
  }
}

export const permissionsStore = {
  state,

  getPermissions (resource) {
    return state.permissionsByResource[keyFor(resource)] || []
  },

  async fetchPermissions (resource, { force = false } = {}) {
    const key = keyFor(resource)

    if (!force && state.permissionsByResource[key]) {
      return state.permissionsByResource[key]
    }

    if (!force && inflight[key]) {
      return inflight[key]
    }

    state.loadingByResource[key] = true
    state.errorByResource[key] = null

    inflight[key] = Api.fetchPermissions(resource).then((json) => {
      state.permissionsByResource[key] = json.data || []
      state.loadedAtByResource[key] = Date.now()

      return state.permissionsByResource[key]
    }).catch((error) => {
      state.errorByResource[key] = error.message || 'Failed to fetch permissions'
      throw error
    }).finally(() => {
      state.loadingByResource[key] = false
      delete inflight[key]
    })

    return inflight[key]
  },

  async createPermission (resource, payload) {
    const key = keyFor(resource)
    const previous = clone(state.permissionsByResource[key] || [])
    const optimistic = {
      id: tempId(),
      action: payload.permission_action,
      subject_type: payload.subject_type,
      subject_id: payload.subject_id,
      resource_type: payload.resource_type,
      resource_id: payload.resource_id,
      inherited: false,
      subject: payload.subject || null,
      optimistic: true
    }

    state.permissionsByResource[key] = [optimistic, ...(state.permissionsByResource[key] || [])]

    try {
      const created = await Api.createPermission(payload)
      replacePermission(key, optimistic.id, created)
      return created
    } catch (error) {
      state.permissionsByResource[key] = previous
      throw error
    }
  },

  async updatePermission (resource, permissionId, nextAction) {
    const key = keyFor(resource)
    const previous = clone(state.permissionsByResource[key] || [])
    const rows = state.permissionsByResource[key] || []
    const row = rows.find((p) => String(p.id) === String(permissionId))

    if (row) {
      row.action = nextAction
      row.optimistic = true
    }

    try {
      const updated = await Api.updatePermission(permissionId, { permission_action: nextAction })
      replacePermission(key, permissionId, updated)
      return updated
    } catch (error) {
      state.permissionsByResource[key] = previous
      throw error
    }
  },

  async removePermission (resource, permissionId) {
    const key = keyFor(resource)
    const previous = clone(state.permissionsByResource[key] || [])

    removePermission(key, permissionId)

    try {
      await Api.deletePermission(permissionId)
    } catch (error) {
      state.permissionsByResource[key] = previous
      throw error
    }
  }
}

